import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useVocabulary } from './contexts/VocabularyContext';
import './css/Vocabulary.css';
import './css/VocabularyQuiz.css';

/*
 * 객관식 수기 채점 표시. 백엔드 vocabularyQuizService 의 MANUAL_CORRECT/WRONG 과 같아야 한다.
 * 종이 채점은 O/X로 하므로 몇 번을 골랐는지 옮겨 적게 하지 않는다.
 */
const MARK_CORRECT = 'O';
const MARK_WRONG = 'X';

// 고른 번호를 기록할 때 쓴다
const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];

/**
 * 답안 일괄 입력 (OMR 답안지).
 *
 * PDF로 뽑은 시험지를 종이로 푼 뒤 답만 옮겨 적는 화면이다.
 * 문제 지문을 일부러 보여주지 않는다 — 종이 시험지가 문제지 역할을 하고,
 * 화면에 지문까지 있으면 옮겨 적을 칸을 찾기가 오히려 번거롭다.
 */
const VocabularyQuizSheet = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quizId } = useParams();
  const { quizApi } = useVocabulary();

  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  // question_id → 채점 표시 ('O' | 'X')
  const [answers, setAnswers] = useState({});
  // question_id → 학생이 쓴 답 (주관식, 선택 사항)
  const [notes, setNotes] = useState({});
  const [recordAnswers, setRecordAnswers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      // 응시용 조회를 그대로 쓴다 — 정답이 내려오지 않으므로 답안지 화면에 안전하다
      const data = await quizApi.getQuiz(quizId);
      if (!data.success) {
        toast.error(t('vocabulary.quiz.notFound'));
        navigate('/vocabulary/quiz');
        return;
      }
      // 마감된 시험도 연다 — 수기 채점은 사람이 하는 일이라 고칠 수 있어야 한다
      setQuiz(data.quiz);
      setQuestions(data.questions);

      /*
       * 기존 채점을 되살린다.
       * 마감된 시험은 서버가 정오(is_correct)를 함께 주므로 그것을 표시로 쓴다.
       * 아직 마감 전이면 O/X 로 저장된 문항만 되살린다.
       */
      const restoredMarks = {};
      const restoredNotes = {};
      data.questions.forEach(q => {
        const prev = String(q.user_answer ?? '');

        if (typeof q.is_correct === 'boolean') {
          restoredMarks[q.question_id] = q.is_correct ? MARK_CORRECT : MARK_WRONG;
        } else if (prev === MARK_CORRECT || prev === MARK_WRONG) {
          restoredMarks[q.question_id] = prev;
        }

        // O/X 는 채점 표시일 뿐 기록된 답이 아니다
        if (q.answered && prev !== MARK_CORRECT && prev !== MARK_WRONG && prev !== '') {
          restoredNotes[q.question_id] = prev;
        }
      });
      setAnswers(restoredMarks);
      setNotes(restoredNotes);
      // 기록된 답이 있으면 처음부터 펼쳐 보여준다
      if (Object.keys(restoredNotes).length > 0) setRecordAnswers(true);
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  // 같은 표시를 다시 누르면 지운다 (잘못 눌렀을 때 되돌릴 방법이 필요하다)
  const toggleMark = (questionId, mark) => {
    setAnswers(prev => {
      const next = { ...prev };
      if (next[questionId] === mark) delete next[questionId];
      else next[questionId] = mark;
      return next;
    });
  };

  // 대부분 맞은 시험이면 전체를 정답으로 깔고 틀린 것만 고치는 편이 빠르다
  const markAll = (mark) => {
    setAnswers(prev => {
      const next = { ...prev };
      questions.forEach(q => { next[q.question_id] = mark; });
      return next;
    });
  };

  // 같은 번호를 다시 누르면 기록을 지운다
  const togglePick = (questionId, choiceIndex) => {
    setNotes(prev => {
      const next = { ...prev };
      if (next[questionId] === String(choiceIndex)) delete next[questionId];
      else next[questionId] = String(choiceIndex);
      return next;
    });
  };

  const filledCount = questions.filter(q => answers[q.question_id] !== undefined).length;
  const isRegrade = quiz?.status === 'completed';

  const handleGrade = async () => {
    /*
     * 채점 결과를 보낸다. 주관식 답을 기록하기로 했으면 함께 실어 보낸다.
     * 정오는 어느 경우에도 사람이 친 O/X를 따른다 (서버가 다시 채점하지 않는다).
     */
    const payload = questions
      .filter(q => answers[q.question_id] !== undefined)
      .map(q => {
        const entry = {
          question_id: q.question_id,
          is_correct: answers[q.question_id] === MARK_CORRECT,
        };
        // 주관식은 쓴 답, 객관식은 고른 번호 — 둘 다 같은 자리에 담긴다
        const written = (notes[q.question_id] ?? '').trim();
        if (recordAnswers && written !== '') {
          entry.user_answer = written;
        }
        return entry;
      });

    if (payload.length === 0) {
      toast.error(t('vocabulary.quiz.sheetEmpty'));
      return;
    }

    const unanswered = questions.length - payload.length;
    // 이미 마감한 시험을 고치는 경우엔 '제출' 경고가 아니라 수정 확인이 맞다
    const message = isRegrade
      ? t('vocabulary.quiz.regradeConfirm')
      : (unanswered > 0
        ? t('vocabulary.quiz.finishWithUnanswered', { count: unanswered })
        : t('vocabulary.quiz.finishConfirm'));
    if (!window.confirm(message)) return;

    setSubmitting(true);
    const data = await quizApi.submitAnswers(quizId, { answers: payload, finish: true });
    setSubmitting(false);

    if (data.success) {
      navigate(`/vocabulary/quiz/${quizId}/result`, { replace: true });
    } else {
      toast.error(t('error.save'));
    }
  };

  if (loading) {
    return <div className="vocab-container"><p className="vocab-empty">{t('vocabulary.loading')}</p></div>;
  }

  return (
    <div className="vocab-container">
      <div className="vocab-header">
        <h2>{quiz.title || t('vocabulary.quiz.untitled')}</h2>
        <button className="vocab-btn" onClick={() => navigate('/vocabulary/quiz')}>
          {t('vocabulary.quiz.backToList')}
        </button>
      </div>

      <div className="sheet-head">
        <h3>{isRegrade ? t('vocabulary.quiz.regradeTitle') : t('vocabulary.quiz.sheetTitle')}</h3>
        <p className="quiz-hint">
          {isRegrade ? t('vocabulary.quiz.regradeHint') : t('vocabulary.quiz.sheetHint')}
        </p>
        <div className="sheet-bulk">
          <span>{t('vocabulary.quiz.markAllLabel', { count: questions.length })}</span>
          <button className="vocab-btn" onClick={() => markAll(MARK_CORRECT)}>
            {t('vocabulary.quiz.markAllCorrect')}
          </button>
          <button className="vocab-btn" onClick={() => markAll(MARK_WRONG)}>
            {t('vocabulary.quiz.markAllWrong')}
          </button>

          {/* 답 기록은 선택 사항이다 — 기본은 O/X만 받아 입력을 최소로 둔다 */}
          <label className="vocab-checkbox sheet-record-toggle">
            <input
              type="checkbox"
              checked={recordAnswers}
              onChange={(e) => setRecordAnswers(e.target.checked)}
            />
            {t('vocabulary.quiz.recordAnswers')}
          </label>
        </div>
      </div>

      <ol className="sheet-grid">
        {questions.map(q => (
          <li className="sheet-row" key={q.question_id}>
            <span className="sheet-num">{q.order_index + 1}</span>

            {/* 객관식·주관식 모두 채점 결과만 받는다 — 종이에 이미 O/X를 쳐 두었다 */}
            <div className="sheet-bubbles">
              <button
                type="button"
                className={`sheet-bubble mark-correct${answers[q.question_id] === MARK_CORRECT ? ' filled' : ''}`}
                onClick={() => toggleMark(q.question_id, MARK_CORRECT)}
                aria-label={`${q.order_index + 1} ${t('vocabulary.quiz.markCorrect')}`}
              >
                ○
              </button>
              <button
                type="button"
                className={`sheet-bubble mark-wrong${answers[q.question_id] === MARK_WRONG ? ' filled' : ''}`}
                onClick={() => toggleMark(q.question_id, MARK_WRONG)}
                aria-label={`${q.order_index + 1} ${t('vocabulary.quiz.markWrong')}`}
              >
                ✕
              </button>
              {/* 어떤 문항인지 구분할 수 있게 형식만 옅게 표시한다 */}
              <span className="sheet-format">
                {q.format === 'multiple_choice'
                  ? t('vocabulary.quiz.formatChoiceShort')
                  : t('vocabulary.quiz.formatShortShort')}
              </span>
            </div>

            {/*
              * 기록 방식이 형식마다 다르다.
              * 주관식은 쓴 답을 그대로, 객관식은 고른 번호를 받는다 —
              * 번호를 텍스트로 치게 하면 종이에서 옮겨 적는 수고가 그대로 남는다.
              */}
            {recordAnswers && q.format === 'short_answer' && (
              <input
                className="sheet-input"
                type="text"
                value={notes[q.question_id] ?? ''}
                onChange={(e) => setNotes(prev => ({ ...prev, [q.question_id]: e.target.value }))}
                placeholder={t('vocabulary.quiz.recordPlaceholder')}
              />
            )}

            {recordAnswers && q.format === 'multiple_choice' && q.choices && (
              <div className="sheet-picked">
                <span className="sheet-picked-label">{t('vocabulary.quiz.pickedLabel')}</span>
                {q.choices.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`sheet-pick${notes[q.question_id] === String(i) ? ' filled' : ''}`}
                    onClick={() => togglePick(q.question_id, i)}
                    aria-label={`${q.order_index + 1} - ${i + 1}`}
                  >
                    {CIRCLED[i] || i + 1}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>

      <div className="sheet-actions">
        <span className="sheet-progress">
          {t('vocabulary.quiz.sheetProgress', { filled: filledCount, total: questions.length })}
        </span>
        <button
          className="vocab-btn vocab-btn-primary"
          onClick={handleGrade}
          disabled={submitting}
        >
          {submitting
            ? t('vocabulary.loading')
            : (isRegrade ? t('vocabulary.quiz.saveRegrade') : t('vocabulary.quiz.gradeNow'))}
        </button>
      </div>
    </div>
  );
};

export default VocabularyQuizSheet;
